import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Container,
  Stack,
  TextField,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Biotech as BiotechIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import aiJobsApi from '../../api/aiJobsApi';

const getStatusChipColor = (status) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const AiJobStatusPage = () => {
  const [jobs, setJobs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: 'submitted_at',
        sort_order: 'DESC'
      };
      const response = await aiJobsApi.getAll(params);
      setJobs(response.jobs);
      setTotalCount(response.totalCount);
    } catch (err) {
      setError('Failed to fetch AI jobs. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <BiotechIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            AI Job Status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor the status of Vision Inferencer API jobs
          </Typography>
        </Box>
      </Stack>

      {/* Search and Filter Controls */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
            Search & Filters
          </Typography>
          <TextField
            placeholder="Search by Job ID, AI Model, or Submitter..."
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ width: '100%' }}
          />
        </Box>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button variant="outlined" size="small" startIcon={<ClearIcon />}>
            Clear Filters
          </Button>
          <Button variant="contained" size="small" startIcon={<RefreshIcon />} onClick={fetchJobs} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
      </Paper>

      {/* Jobs Table */}
      <Paper sx={{ p: 3, borderRadius: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <BiotechIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Jobs ({totalCount})
          </Typography>
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Inspection</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Images</TableCell>
                <TableCell>AI Model</TableCell>
                <TableCell>Submitter</TableCell>
                <TableCell>Submitted At</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Alert severity="error">{error}</Alert>
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No jobs to display
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{job.job_id}</TableCell>
                    <TableCell>{job.inspection_id}</TableCell>
                    <TableCell>{job.asset_name} (ID: {job.asset_id})</TableCell>
                    <TableCell>{job.image_count}</TableCell>
                    <TableCell>{job.ai_model_id || `${job.ai_model_name} (v${job.ai_model_version})`}</TableCell>
                    <TableCell>{job.submitted_by_name || 'N/A'}</TableCell>
                    <TableCell>{format(new Date(job.submitted_at), 'dd MMM yyyy, hh:mm a')}</TableCell>
                    <TableCell>
                      <Chip
                        label={job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        color={getStatusChipColor(job.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Container>
  );
};

export default AiJobStatusPage;